// Ported from dev-scrips-pm-react src/billing/components/invoice-service-order-source.tsx (PROD-3312 appointment Billing tab). PM primitives come from the
// PROD-3310 billing bridge (`@/billing/bridge`); PM's arbitrary-value classes are the named utilities on the same tokens.
import { BridgeStatusChip } from '@/billing/bridge';
import { useState, type ReactElement } from 'react';
import { Check, Trash2, Undo2 } from 'lucide-react';

import { BridgeButton as Button } from '@/billing/bridge';
import { Popover, PopoverContent, PopoverTrigger } from '@/billing/bridge';
import { BridgeTooltipTrigger as TooltipTrigger, Tooltip, TooltipContent } from '@/billing/bridge';
import { LINE_CHIP_CLASS, addedByLabel } from '@/billing/lib/line-chip';
import { formatOrderTime, type InvoiceServiceOrderInfo } from '@/billing/lib/pending-orders';
import type { InvoiceService } from '@/shared/types/billing';
import { ROW_ICON_BUTTON } from '@/billing/bridge';

interface MarkerProps {
  service: InvoiceService;
  info: InvoiceServiceOrderInfo | undefined;
}

/**
 * Under the line name: "From order · Lab", the "Added by name · time" chip
 * ("Added · time" when the name is unknown; same chip as the member price) with
 * its "Added by name, time" tooltip,
 * and "Order removed from plan" when the order has since left the plan.
 */
export function OrderSourceMarker({ service, info }: MarkerProps) {
  const who = service.addedByName?.trim() || undefined;
  const when = formatOrderTime(info?.addedOn);
  const tooltip = `Added by ${who ?? 'a biller'}${when ? `, ${when}` : ''}`;
  return (
    <>
      <div className="text-xs text-text-muted">
        From order{info?.orderType ? ` · ${info.orderType}` : ''}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Tooltip>
          <TooltipTrigger
            render={
              <span className={LINE_CHIP_CLASS} tabIndex={0}>
                <span>{addedByLabel(who)}</span>
                {when && <span className="tabular-nums">· {when}</span>}
              </span>
            }
          />
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
        {info?.removedFromPlan && (
          <BridgeStatusChip semantic="warning" tone="soft" size="xs" className="mt-1">
            Order removed from plan
          </BridgeStatusChip>
        )}
      </div>
    </>
  );
}

interface ActionsProps {
  sourceOrderKey: string;
  /** The line's name, for the confirm title. */
  display: string;
  busy: boolean;
  onKeep: (sourceOrderKey: string) => void;
  onDrop: (sourceOrderKey: string) => void;
}

/**
 * Keep / Drop for a line whose order left the plan (canvas board 6, C2). Keep
 * is UI only: the line stays and Issue unblocks. Drop takes the line off the
 * invoice (`POST .../Orders/revert`) after a confirm (PROD-2678); it never
 * changes the order.
 */
export function RemovedOrderActions({ sourceOrderKey, display, busy, onKeep, onDrop }: ActionsProps) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Button variant="outline" size="sm" disabled={busy} onClick={() => onKeep(sourceOrderKey)}>
        <Check aria-hidden /> Keep
      </Button>
      <ConfirmLinePopover
        title={`Drop ${display} from this invoice?`}
        description="The line is removed from this invoice. The order is not changed."
        confirmLabel="Drop"
        busy={busy}
        onConfirm={() => onDrop(sourceOrderKey)}
        trigger={
          <Button variant="destructive" size="sm" disabled={busy}>
            <Trash2 aria-hidden /> Drop
          </Button>
        }
      />
    </div>
  );
}

interface RevertProps {
  sourceOrderKey: string;
  /** The line's name, for the confirm title. */
  display: string;
  busy: boolean;
  onRevert: (sourceOrderKey: string) => void;
}

/**
 * "Revert to pending": beside the × on a Draft line added from an order. It
 * takes the line off the invoice, so it asks first (PROD-2678).
 */
export function RevertToPendingButton({ sourceOrderKey, display, busy, onRevert }: RevertProps) {
  return (
    <ConfirmLinePopover
      title={`Revert ${display} to pending?`}
      description="The line is removed from this invoice."
      confirmLabel="Revert"
      tooltip="Revert to pending orders"
      busy={busy}
      onConfirm={() => onRevert(sourceOrderKey)}
      trigger={
        <button
          type="button"
          disabled={busy}
          aria-label="Revert to pending"
          className={`${ROW_ICON_BUTTON} text-text-secondary`}
        >
          <Undo2 className="size-4" aria-hidden />
        </button>
      }
    />
  );
}

interface ConfirmLinePopoverProps {
  title: string;
  description: string;
  confirmLabel: string;
  /** The button that opens the confirm. */
  trigger: ReactElement;
  /** Optional styled tooltip on the trigger. */
  tooltip?: string;
  busy: boolean;
  onConfirm: () => void;
}

/** A small Cancel / confirm popover in the `DismissOrdersPopover` shape. */
function ConfirmLinePopover({
  title,
  description,
  confirmLabel,
  trigger,
  tooltip,
  busy,
  onConfirm,
}: ConfirmLinePopoverProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger render={<PopoverTrigger render={trigger} />} />
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        <PopoverTrigger render={trigger} />
      )}
      <PopoverContent align="end" className="w-72" aria-label={title}>
        <div className="flex flex-col gap-1">
          <div className="text-sm font-medium text-text-primary">{title}</div>
          <div className="text-xs text-text-muted">{description}</div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border-default pt-2.5">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={busy}
            onClick={() => {
              onConfirm();
              setOpen(false);
            }}
          >
            {confirmLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
